/**
 * Vendor Entity Dashboard
 *
 * Comprehensive dashboard for vendor users with:
 * - Available service requests overview
 * - Active work orders management  
 * - Portfolio and performance metrics
 * - Quick actions and notifications
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  useVendorServiceRequests,
  useVendorWorkOrders,
  UseVendorServiceRequestsOptions,
} from '@/hooks/entities/vendor';
import { useEntityContext } from '@/hooks/entities/vendor';
import { useAuth } from '@/contexts/AuthContext';
import { ServiceRequestCard } from '@/components/entities/vendor/services/ServiceRequestCard';
import { VendorBiddingModal } from '@/components/entities/vendor/marketplace/VendorBiddingModal';
import { useNavigate } from 'react-router-dom';
import { ServiceRequest } from '@/types/entities/vendor/serviceRequest';
import type {
  ServiceRequestCategory,
  ServiceRequestPriority,
  ServiceRequestStatus,
} from '@/types/entities/vendor/serviceRequest';
import type { UserRole } from '@/types/shared/auth';
import {
  Briefcase,
  Clock,
  DollarSign,
  Star,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Calendar,
  MapPin,
  User,
  Users,
  Award,
  Timer,
  MessageSquare,
} from 'lucide-react';

// Type definition for bid data structure used in VendorBiddingModal
interface BidData {
  estimatedCost: string;
  estimatedDuration: string;
  availability: string;
  message: string;
  guarantees: string[];
  customGuarantees: string;
  emergencyService: boolean;
  materialsIncluded: boolean;
  followUpIncluded: boolean;
  portfolioItems: File[];
  responseTime: string;
  terms: {
    depositRequired: boolean;
    depositAmount: string;
    paymentTerms: string;
    cancellationPolicy: string;
  };
}

export function VendorDashboard() {
  const { user } = useAuth();
  const { entityType, capabilities } = useEntityContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequest | null>(null);
  const [showBiddingModal, setShowBiddingModal] = useState(false);

  const vendorOptions: UseVendorServiceRequestsOptions = {
    vendorId: user?.citizenId || '',
    autoFetch: true,
    filters: undefined,
  };

  const {
    availableRequests,
    assignedRequests,
    isLoading: requestsLoading,
    error: requestsError,
    refresh: refreshRequests,
  } = useVendorServiceRequests(vendorOptions);

  const {
    workOrders,
    isLoading: ordersLoading,
    error: ordersError,
    refresh: refreshOrders,
  } = useVendorWorkOrders(user?.citizenId || '');

  // Mock group buy opportunities with token integration
  const mockGroupBuyOpportunities = [
    {
      id: 'gb-001',
      title: 'Bulk HVAC Filter Replacement',
      description: 'Building A - 24 units requesting HVAC filter replacement service',
      category: 'hvac',
      participants: 24,
      totalValue: 1800,
      averagePerUnit: 75,
      deadline: '2025-08-25',
      priority: 'medium',
      groupTokens: 450,
      estimatedSavings: '25-35%',
      location: { building: 'Building A', commonArea: 'All Units' },
      timelineFlexible: true,
    },
    {
      id: 'gb-002',
      title: 'Coordinated Carpet Cleaning',
      description: 'Building B & C - Professional carpet cleaning service coordination',
      category: 'cleaning',
      participants: 18,
      totalValue: 2700,
      averagePerUnit: 150,
      deadline: '2025-08-30',
      priority: 'low',
      groupTokens: 675,
      estimatedSavings: '20-30%',
      location: { building: 'Building B & C', commonArea: 'Residential Units' },
      timelineFlexible: false,
    },
  ];

  const handleBidOnRequest = (request: ServiceRequest) => {
    setSelectedRequest(request);
    setShowBiddingModal(true);
  };

  const handleSubmitBid = (bidData: BidData) => {
    console.log('Submitting bid:', bidData);
    setShowBiddingModal(false);
    refreshRequests();
  };

  // Mock vendor profile for bidding modal
  const mockVendorProfile = {
    id: user?.citizenId || 'vendor-123',
    name: user?.displayName || 'Vendor Services',
    rating: 4.8,
    reviews: 127,
    certifications: ['Licensed Contractor', 'Insured', 'Background Checked'],
    specialties: ['Plumbing', 'Electrical', 'HVAC', 'General Maintenance'],
    responseTime: 'Usually responds within 2 hours',
    completedJobs: 94,
    guarantees: ['Work Guarantee', 'Licensed & Insured', 'Same-day Service Available'],
  };

  // Dashboard metrics
  const metrics = {
    totalEarnings: workOrders
      .filter((order) => order.status === 'completed')
      .reduce((sum, order) => sum + (order.scope?.totalCost || 0), 0),
    activeJobs: workOrders.filter((order) => order.status === 'in_progress').length,
    completedJobs: workOrders.filter((order) => order.status === 'completed').length,
    averageRating: 4.8, // This would come from user profile/reviews
    newRequests: availableRequests.length,
    pendingProposals: assignedRequests.filter((req) => req.status === 'in_review').length,
  };

  const quickActions = [
    {
      title: 'Browse New Requests',
      description: 'Find new service opportunities',
      icon: Briefcase,
      action: () => setActiveTab('available'),
      count: metrics.newRequests,
    },
    {
      title: 'Manage Work Orders',
      description: 'Update ongoing projects',
      icon: Clock,
      action: () => setActiveTab('workorders'),
      count: metrics.activeJobs,
    },
    {
      title: 'Update Portfolio',
      description: 'Showcase your work',
      icon: Star,
      action: () => navigate('/marketplace/work-portfolio'),
      count: 0,
    },
    {
      title: 'Manage Services',
      description: 'Update service catalog',
      icon: DollarSign,
      action: () => navigate('/marketplace/service-catalog'),
      count: 0,
    },
  ];

  const upcomingDeadlines = workOrders
    .filter((order) => order.assignment?.estimatedCompletionDate && order.status === 'in_progress')
    .sort(
      (a, b) =>
        new Date(a.assignment!.estimatedCompletionDate!).getTime() -
        new Date(b.assignment!.estimatedCompletionDate!).getTime(),
    )
    .slice(0, 5);

  const recentActivity = [
    ...assignedRequests.slice(0, 3).map((req) => ({
      type: 'request',
      title: `New assignment: ${req.title}`,
      time: req.updatedAt,
      priority: req.priority,
    })),
    ...workOrders.slice(0, 2).map((order) => ({
      type: 'workorder',
      title: `Work order updated: ${order.title}`,
      time: order.updatedAt,
      priority: order.priority,
    })),
  ]
    .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
    .slice(0, 5);

  if (!capabilities?.canViewAvailableRequests) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Access denied. Vendor permissions required.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Vendor Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.displayName}! Here's your business overview.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refreshRequests()} variant="outline">
            Refresh Requests
          </Button>
          <Button onClick={() => refreshOrders()} variant="outline">
            Refresh Orders
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${metrics.totalEarnings.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              From {metrics.completedJobs} completed jobs
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Jobs</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeJobs}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.pendingProposals} pending proposals
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.averageRating}</div>
            <p className="text-xs text-muted-foreground">Based on customer reviews</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Opportunities</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.newRequests}</div>
            <p className="text-xs text-muted-foreground">Available service requests</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Dashboard Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="available">Open Bids ({metrics.newRequests})</TabsTrigger>
          <TabsTrigger value="group-buys">
            Group Buys ({mockGroupBuyOpportunities.length})
          </TabsTrigger>
          <TabsTrigger value="assigned">Assigned ({assignedRequests.length})</TabsTrigger>
          <TabsTrigger value="workorders">Work Orders ({workOrders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks and shortcuts</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  {quickActions.map((action, index) => {
                    const IconComponent = action.icon;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                        onClick={action.action}
                      >
                        <div className="flex items-center gap-3">
                          <IconComponent className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{action.title}</p>
                            <p className="text-sm text-muted-foreground">{action.description}</p>
                          </div>
                        </div>
                        {action.count > 0 && <Badge variant="secondary">{action.count}</Badge>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest updates and changes</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 p-2">
                        <div className="mt-1">
                          {activity.type === 'request' ? (
                            <Briefcase className="h-4 w-4 text-blue-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{activity.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(activity.time).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge
                          variant={activity.priority === 'urgent' ? 'destructive' : 'outline'}
                          className="text-xs"
                        >
                          {activity.priority}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Deadlines */}
          {upcomingDeadlines.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Upcoming Deadlines
                </CardTitle>
                <CardDescription>Projects that need attention soon</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingDeadlines.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{order.title}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{order.location?.building}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">
                          {new Date(
                            order.assignment?.estimatedCompletionDate || '',
                          ).toLocaleDateString()}
                        </p>
                        <Badge variant={order.priority === 'urgent' ? 'destructive' : 'secondary'}>
                          {order.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="available" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Open Bidding Opportunities</CardTitle>
              <CardDescription>Submit competitive bids for new service requests</CardDescription>
            </CardHeader>
            <CardContent>
              {requestsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">Loading opportunities...</p>
                </div>
              ) : availableRequests.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-medium mb-2">No Open Opportunities</h3>
                  <p className="text-sm text-muted-foreground">
                    Check back soon for new service requests that match your expertise.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableRequests.map((request) => (
                    <div
                      key={request.id}
                      className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{request.title}</h3>
                            <Badge
                              variant={request.priority === 'urgent' ? 'destructive' : 'secondary'}
                            >
                              {request.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {request.description?.slice(0, 120)}...
                          </p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span>{request.location?.building || 'Location TBD'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Timer className="w-3 h-3" />
                              <span>Posted {new Date(request.createdAt).toLocaleDateString()}</span>
                            </div>
                            {request.budget && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                <span>
                                  ${request.budget.min} - ${request.budget.max}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant="outline" className="text-xs">
                            {request.category}
                          </Badge>
                          <Button size="sm" onClick={() => handleBidOnRequest(request)}>
                            Submit Bid
                          </Button>
                        </div>
                      </div>

                      {request.timeline && (
                        <div className="flex items-center gap-4 text-xs bg-muted/50 rounded p-2">
                          <span className="font-medium">Timeline:</span>
                          {request.timeline.preferredStartDate && (
                            <span>
                              Start:{' '}
                              {new Date(request.timeline.preferredStartDate).toLocaleDateString()}
                            </span>
                          )}
                          {request.timeline.deadline && (
                            <span>
                              Deadline: {new Date(request.timeline.deadline).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="group-buys" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Group Buy Opportunities
              </CardTitle>
              <CardDescription>
                Bulk service requests with enhanced earning potential and group tokens
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockGroupBuyOpportunities.map((groupBuy) => (
                  <div
                    key={groupBuy.id}
                    className="border rounded-lg p-4 bg-linear-to-r from-purple-50 to-blue-50"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{groupBuy.title}</h3>
                          <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                            Group Buy
                          </Badge>
                          <Badge
                            variant={groupBuy.priority === 'urgent' ? 'destructive' : 'outline'}
                          >
                            {groupBuy.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{groupBuy.description}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Participants:</span>
                            <div className="font-semibold text-purple-700">
                              {groupBuy.participants} units
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Total Value:</span>
                            <div className="font-semibold text-green-600">
                              ${groupBuy.totalValue.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg Per Unit:</span>
                            <div className="font-semibold">${groupBuy.averagePerUnit}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Group Tokens:</span>
                            <div className="font-semibold text-blue-600">
                              {groupBuy.groupTokens}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right text-xs text-muted-foreground">
                          Deadline: {new Date(groupBuy.deadline).toLocaleDateString()}
                        </div>
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() =>
                            handleBidOnRequest({
                              id: groupBuy.id,
                              title: groupBuy.title,
                              description: groupBuy.description,
                              category: groupBuy.category as ServiceRequestCategory,
                              priority: groupBuy.priority as ServiceRequestPriority,
                              status: 'open' as ServiceRequestStatus,
                              requestedBy: {
                                id: 'group-buy-coordinator',
                                name: 'Group Buy Coordinator',
                                role: 'resident' as UserRole,
                              },
                              location: groupBuy.location,
                              budget: {
                                min: groupBuy.averagePerUnit * groupBuy.participants * 0.8,
                                max: groupBuy.totalValue,
                                currency: 'USD',
                              },
                              timeline: {
                                deadline: groupBuy.deadline,
                              },
                              attachments: [],
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                            })
                          }
                        >
                          Bid on Group Buy
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between bg-white/50 rounded p-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-purple-600" />
                        <span className="font-medium">
                          Estimated Savings: {groupBuy.estimatedSavings}
                        </span>
                      </div>
                      <div className="text-muted-foreground">
                        vs individual requests • Timeline{' '}
                        {groupBuy.timelineFlexible ? 'Flexible' : 'Fixed'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assigned" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Assigned Service Requests</CardTitle>
              <CardDescription>Requests you've responded to or been assigned</CardDescription>
            </CardHeader>
            <CardContent>
              {requestsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">Loading assigned requests...</p>
                </div>
              ) : assignedRequests.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">No assigned requests</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {assignedRequests.map((request) => (
                    <ServiceRequestCard
                      key={request.id}
                      serviceRequest={request}
                      userRole={'vendor' as const}
                      userId={user?.citizenId}
                      onView={(req) => console.log('View details:', req.id)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workorders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Work Orders</CardTitle>
              <CardDescription>Manage your active and completed work orders</CardDescription>
            </CardHeader>
            <CardContent>
              {ordersLoading ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">Loading work orders...</p>
                </div>
              ) : workOrders.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-muted-foreground">No work orders</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {workOrders.map((order) => (
                    <Card key={order.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{order.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{order.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>{order.requestedBy.name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>{order.location?.building}</span>
                            </div>
                            {order.scope?.totalCost && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                <span>${order.scope.totalCost}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge
                            variant={
                              order.status === 'completed'
                                ? 'default'
                                : order.status === 'in_progress'
                                  ? 'secondary'
                                  : 'outline'
                            }
                          >
                            {order.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline">{order.priority}</Badge>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bidding Modal */}
      {selectedRequest && (
        <VendorBiddingModal
          isOpen={showBiddingModal}
          onClose={() => setShowBiddingModal(false)}
          serviceRequest={selectedRequest}
          vendorProfile={mockVendorProfile}
          onSubmitBid={handleSubmitBid}
        />
      )}
    </div>
  );
}
